import { ILimitset } from "../../../components/views/betslipV2/betslip.models";
import {
  BET_TYPES,
  BET_TYPES_DISPLAY_NAMES,
  STAKE_DISTRIBUTION
} from "../../../components/views/luckyPickV3/helpers/luckyPickConstants";
import { IBetslipBetType } from "../BetslipV3/types/types";
import { ILuckyPick, ILuckyPickBetType } from "./luckyPickTypes";

export const getOdds = (horses: IBetslipBetType[]): number => {
  if (!horses.length) return 0;

  return horses
    ?.map((a) => a.eventDetailOfferedOdds ?? 0)
    .reduce((c, d) => (c + 1) * (d + 1) - 1, 0);
};

export const getPayouts = (
  horses: IBetslipBetType[],
  stake: number
): number => {
  if (!horses.length) return 0;
  const odds = getOdds(horses);

  return odds * stake + stake;
};

const isBetTypeActive = (betType: ILuckyPickBetType) => {
  return betType.horses.length > 0;
};

export const updateTotalStakeAndPayout = (
  winDouble: ILuckyPickBetType,
  placeTreble: ILuckyPickBetType,
  winPlaceDouble: ILuckyPickBetType
) => {
  let winDoubleData = { stake: 0, payout: 0 };
  let placeTrebleData = { stake: 0, payout: 0 };
  let winPlaceDoubleData = { stake: 0, payout: 0 };

  if (isBetTypeActive(winDouble))
    winDoubleData = { stake: winDouble.stake, payout: winDouble.payout };

  if (isBetTypeActive(placeTreble))
    placeTrebleData = { stake: placeTreble.stake, payout: placeTreble.payout };

  if (isBetTypeActive(winPlaceDouble))
    winPlaceDoubleData = {
      stake: winPlaceDouble.stake,
      payout: winPlaceDouble.payout
    };

  const updatedStake =
    winDoubleData.stake + placeTrebleData.stake + winPlaceDoubleData.stake;
  const updatedPayout =
    winDoubleData.payout + placeTrebleData.payout + winPlaceDoubleData.payout;

  return { updatedStake, updatedPayout };
};

export const checkIfPayoutIsOverLimit = (
  betType: string,
  horses: IBetslipBetType[],
  payout: number,
  limits: { [Key: string]: ILimitset }
) => {
  const hasFirstTimer = horses.find((horse) => horse.firstTimer);

  let limit = 0;
  if (
    betType === BET_TYPES.WIN_DOUBLE ||
    betType === BET_TYPES.WIN_PLACE_DOUBLE
  )
    limit = hasFirstTimer
      ? limits?.firstTimerLimits?.double
      : limits?.regularLimits?.double;
  if (betType === BET_TYPES.PLACE_TREBLE)
    limit = hasFirstTimer
      ? limits?.firstTimerLimits?.treble
      : limits?.regularLimits?.treble;

  return payout > limit;
};

export const isInvalidBet = (bet: ILuckyPickBetType) => {
  return bet.horses.length === 0 || bet.stake < 1 || bet.isPayoutOverLimit;
};

export const areAllLuckyPickBetsAvailable = (
  winDouble: ILuckyPickBetType,
  placeTreble: ILuckyPickBetType,
  winPlaceDouble: ILuckyPickBetType
): boolean => {
  const isWinDoubleAvailable = winDouble.horses.length > 0;
  const isPlaceTrebleAvailable = placeTreble.horses.length > 0;
  const isWinPlaceDoubleAvailable = winPlaceDouble.horses.length > 0;

  return (
    isWinDoubleAvailable && isPlaceTrebleAvailable && isWinPlaceDoubleAvailable
  );
};

export const areAllPlaceBetsAvailable = (state: ILuckyPick): boolean => {
  const { placeTreble, winPlaceDouble } = state;

  const isPlaceTrebleAvailable = placeTreble.horses.length > 0;
  const isWinPlaceDoubleAvailable = winPlaceDouble.horses.length > 0;

  return isPlaceTrebleAvailable && isWinPlaceDoubleAvailable;
};

export const areAllPlaceBetsMissing = (state: ILuckyPick): boolean => {
  const { placeTreble, winPlaceDouble } = state;

  const isPlaceTrebleAvailable = placeTreble.horses.length > 0;
  const isWinPlaceDoubleAvailable = winPlaceDouble.horses.length > 0;

  return !isPlaceTrebleAvailable && !isWinPlaceDoubleAvailable;
};

export const getNewStakeFactorForWinDouble = (state: ILuckyPick): number => {
  if (areAllPlaceBetsAvailable(state)) return STAKE_DISTRIBUTION.WIN_DOUBLE;
  if (areAllPlaceBetsMissing(state))
    return STAKE_DISTRIBUTION.WIN_DOUBLE_WITH_NO_PLACE_BET;

  return STAKE_DISTRIBUTION.WIN_DOUBLE_WITH_ONE_PLACE_BET;
};

export const getNewStakeFactorForPlaceTreble = (state: ILuckyPick) => {
  const { winPlaceDouble } = state;

  const isWinPlaceDoubleAvailable = winPlaceDouble.horses.length > 0;
  if (!isWinPlaceDoubleAvailable)
    return STAKE_DISTRIBUTION.PLACE_TREBLE_AS_ONLY_PLACE_BET;

  return STAKE_DISTRIBUTION.PLACE_TREBLE;
};

export const getNewStakeFactorForWinPlaceDouble = (state: ILuckyPick) => {
  const { placeTreble } = state;
  const isPlaceTrebleAvailable = placeTreble.horses.length > 0;
  if (!isPlaceTrebleAvailable)
    return STAKE_DISTRIBUTION.WIN_PLACE_DOUBLE_AS_ONLY_PLACE_BET;

  return STAKE_DISTRIBUTION.WIN_PLACE_DOUBLE;
};

export const getCombinationInfo = (
  title: string,
  legsPerBet: number,
  totalStake: number,
  totalOdds: number,
  payout: number,
  horses: IBetslipBetType[],
  punterId: number
) => {
  return {
    combinatonInfo: {
      betTitle: title,
      combinationBetTypeId: 3,
      legsPerBet,
      totalStake,
      totalOdds,
      payout,
      guid: null,
      pid: punterId
    },
    dt: [...horses]
  };
};

export const getLuckyPickBet = (
  betTypesData: { type: string; data: ILuckyPickBetType }[],
  punterId: number,
  meetingId: number
) => {
  const tempBets = betTypesData
    .filter(
      (bet) =>
        bet.data.horses.length > 0 &&
        bet.data.stake >= 1 &&
        !bet.data.isPayoutOverLimit
    )
    .map((item) => {
      const { horses, stake, odds, payout } = item.data;

      const legsPerBet =
        item.type === BET_TYPES_DISPLAY_NAMES[BET_TYPES.PLACE_TREBLE] ? 3 : 2;

      const correctBetType =
        item.type === BET_TYPES_DISPLAY_NAMES[BET_TYPES.WIN_DOUBLE] ||
        item.type === BET_TYPES_DISPLAY_NAMES[BET_TYPES.WIN_PLACE_DOUBLE]
          ? "DOUBLES"
          : "TREBLES";

      return getCombinationInfo(
        correctBetType,
        legsPerBet,
        stake,
        odds,
        payout,
        horses,
        punterId
      );
    });

  const final = {
    bets: [...tempBets],
    singleBet: {
      SingleWinStake: 0,
      SingleWinHorses: null,
      SinglePlaceStake: 0,
      SinglePlaceHorses: null,
      PID: punterId
    },
    isLuckyPick: true,
    meetingId
  };

  return final;
};
